// useLivrosAdquiridos.js — Os livros que já estão na conta do usuário.
//
// A lista deixou de ser uma coisa só. Hoje o backend devolve a UNIÃO de duas
// que não são a mesma:
//
//   `livros_adquiridos` = e-books de que a pessoa é DONA (comprou o digital);
//   brinde              = e-books que ela pode LER porque comprou o impresso.
//
// Cada item vem com `origem` ("compra" | "brinde_fisico") e `pode_baixar`, e é
// por isso que este hook responde TRÊS perguntas em vez de uma — o motivo de
// cada uma está junto delas, mais abaixo.
//
// GET {API}api/usuario-editora/livros-usuario-editora  (Bearer)
import { useState, useEffect, useCallback } from "react";

const API_URL = process.env.REACT_APP_API;

const pegarToken = () =>
  localStorage.getItem("token" + (process.env.REACT_APP_USER_FUNCTION || ""));

const useLivrosAdquiridos = () => {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const buscar = useCallback(async () => {
    const token = pegarToken();

    // Deslogado: lista vazia e `loading` FALSO. Antes o loading ficava preso
    // em true para sempre nesse caso, e quem dependesse dele nunca saía do
    // estado de carregando.
    if (!token) {
      setBooks([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const resp = await fetch(`${API_URL}api/usuario-editora/livros-usuario-editora`, {
        method: "GET",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
      });
      if (!resp.ok) throw new Error("Erro ao obter os livros do usuário.");
      const dados = await resp.json();
      setBooks(Array.isArray(dados) ? dados : []);
    } catch (e) {
      setError(e.message);
      setBooks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    buscar();
  }, [buscar]);

  /** Acha o item da estante. Compara como texto: o id vem number da API e
   *  string da rota (`useParams`). Item sem `origem` é resposta de uma API
   *  anterior ao brinde, quando a lista era só `livros_adquiridos` — ou seja,
   *  compra. */
  const achar = useCallback(
    (id) => books.find((l) => String(l.id) === String(id)) || null,
    [books]
  );

  // TRÊS perguntas, e não uma, porque as telas perguntam coisas diferentes
  // sobre a mesma lista:
  //
  //   possui         — "é dono do e-book?" É o que liga o aviso "Você já
  //                    possui" no cartão de preço, e esse aviso ESCONDE o
  //                    botão de compra. Se o brinde entrasse aqui, quem ganhou
  //                    a leitura ao comprar o impresso deixaria de conseguir
  //                    comprar o e-book — justamente a venda que sobraria.
  //   podeLer        — "abre no leitor?" Compra e brinde abrem os dois; é a
  //                    pergunta que corresponde ao que o servidor responde em
  //                    /leitor/{id}.
  //   ganhouDeBrinde — "veio do impresso?" Muda o rótulo na estante e tira o
  //                    download, que o servidor recusa de qualquer forma
  //                    (`pode_baixar` falso): o comprador é dono do papel, não
  //                    do arquivo.
  //
  // Uma função só serviria mal as três, e o erro apareceria como venda
  // perdida, não como tela quebrada — que é o tipo de defeito que ninguém
  // reporta.
  const possui = useCallback((id) => {
    const l = achar(id);
    return !!l && l.origem !== "brinde_fisico";
  }, [achar]);

  const podeLer = useCallback((id) => !!achar(id), [achar]);

  const ganhouDeBrinde = useCallback(
    (id) => achar(id)?.origem === "brinde_fisico",
    [achar]
  );

  return { books, loading, error, possui, podeLer, ganhouDeBrinde, recarregar: buscar };
};

export default useLivrosAdquiridos;
